import {useReducer} from 'react';

import {
  WarehouseModelData,
  WarehouseRelationshipData,
  WarehouseTableData,
} from 'insights/warehouse/warehouseModelsDataLayout';
import {Point} from 'util/geometry/GeometryConstants';
import {assertNever} from 'util/Types';

import {DWD_MAXIMUM_NUMBER_TABLES_TO_ADD} from './diagramLayoutConstants';
import {
  addTablesToLayout,
  cellIsObscured,
  cellToTopLeftPixel,
  columnIsShown,
  getNewTableCellsNear,
  getUnpopulatedRelatedTables,
  initializeDiagramLayout,
} from './diagramLayoutHelpers';
import {
  DiagramGridCell,
  DiagramLayout,
  DiagramPixelPoint,
  WarehouseRelationshipPath,
} from './diagramLayoutTypes';
import getRelationshipPath from './getRelationshipPath';

export enum DiagramLayoutActionType {
  ADD_TABLE_AT_CELL = 'ADD_TABLE_AT_CELL',
  ADD_TABLES = 'ADD_TABLES',
  COLLAPSE_TABLE = 'COLLAPSE_TABLE',
  EXPAND_TABLE = 'EXPAND_TABLE',
  HIDE_RELATIONSHIP_TOOLTIP = 'HIDE_RELATIONSHIP_TOOLTIP',
  SHOW_RELATIONSHIP_TOOLTIP = 'SHOW_RELATIONSHIP_TOOLTIP',
  START_HOVER_RELATIONSHIP = 'START_HOVER_RELATIONSHIP',
  STOP_HOVER_RELATIONSHIP = 'STOP_HOVER_RELATIONSHIP',
}

export type DiagramLayoutAction =
  | {type: DiagramLayoutActionType.ADD_TABLE_AT_CELL; tableName: string; cell: DiagramGridCell}
  | {
      type: DiagramLayoutActionType.ADD_TABLES;
      tableNames: Array<string>;
      cells: Array<DiagramGridCell>;
    }
  | {type: DiagramLayoutActionType.COLLAPSE_TABLE; tableName: string}
  | {type: DiagramLayoutActionType.EXPAND_TABLE; tableName: string}
  | {type: DiagramLayoutActionType.HIDE_RELATIONSHIP_TOOLTIP}
  | {
      type: DiagramLayoutActionType.SHOW_RELATIONSHIP_TOOLTIP;
      relationship: WarehouseRelationshipData;
      triggerPoint: Point;
    }
  | {type: DiagramLayoutActionType.START_HOVER_RELATIONSHIP; relationship: WarehouseRelationshipData}
  | {type: DiagramLayoutActionType.STOP_HOVER_RELATIONSHIP};

export function diagramLayoutReducer(
  diagramLayout: DiagramLayout,
  action: DiagramLayoutAction
): DiagramLayout {
  switch (action.type) {
    case DiagramLayoutActionType.ADD_TABLE_AT_CELL:
      return addTablesToLayout(diagramLayout, [action.tableName], [action.cell]);
    case DiagramLayoutActionType.ADD_TABLES:
      return addTablesToLayout(diagramLayout, action.tableNames, action.cells);
    case DiagramLayoutActionType.COLLAPSE_TABLE:
      return {
        ...diagramLayout,
        verticallyExpandedTables: diagramLayout.verticallyExpandedTables.delete(action.tableName),
      };
    case DiagramLayoutActionType.EXPAND_TABLE:
      return {
        ...diagramLayout,
        verticallyExpandedTables: diagramLayout.verticallyExpandedTables.add(action.tableName),
      };
    case DiagramLayoutActionType.HIDE_RELATIONSHIP_TOOLTIP:
      return diagramLayout.relationshipTooltip == null
        ? diagramLayout
        : {
            ...diagramLayout,
            relationshipTooltip: null,
          };
    case DiagramLayoutActionType.SHOW_RELATIONSHIP_TOOLTIP:
      return {
        ...diagramLayout,
        relationshipTooltip: {
          relationshipData: action.relationship,
          triggerPoint: action.triggerPoint,
        },
      };
    case DiagramLayoutActionType.START_HOVER_RELATIONSHIP:
      return {...diagramLayout, hoveredRelationship: action.relationship};
    case DiagramLayoutActionType.STOP_HOVER_RELATIONSHIP:
      return {...diagramLayout, hoveredRelationship: null};
    default:
      throw new Error(`Invalid action type ${assertNever(action)}`);
  }
}

/**
 * @param layout: DiagramLayout currently being used for rendering the diagram.
 * @param addRelatedTables: Take a table and add all tables with relations to that table to the diagram
 *     if they have not yet been added.
 * @param cellIsObscured: Determine whether or not a grid cell is obscured by a table above it that has
 *     been expanded to show all of its columns.
 * @param collapseTableColumns: Show only <= DWD_MAX_COLLAPSED_COLUMNS_SHOWN columns for the given table.
 * @param expandTableColumns: Show all columns of the input table.
 * @param getCellTopLeftPixelPoint: Get the translation from top left of (0, 0) to top-left of the input.
 * @param getRelationshipPath: Get the collection of points connecting two related columns on the diagram.
 * @param shouldShowRelationship: Determine whether or not a relationship should be shown (whether or not
 *     both columns are being displayed on the diagram).
 * @param startHoverRelationship: Start hovering a particular relationship, to highlight it and its source/
 *     target columns.
 * @param stopHoverRelationship: Stop hovering all relationships.
 * @param _addTableAtCell: Take a table and a cell and add the table to the diagram at the given point.
 *     Overwrites any table already in the input cell. Currently, this is for testing convenience.
 */
export interface UseDiagramLayoutStateOutput {
  layout: DiagramLayout;
  addRelatedTables: (table: WarehouseTableData) => void;
  cellIsObscured: (cell: DiagramGridCell, models: WarehouseModelData) => boolean;
  collapseTableColumns: (tableName: string) => void;
  expandTableColumns: (tableName: string) => void;
  getCellTopLeftPixelPoint: (cell: DiagramGridCell) => DiagramPixelPoint;
  getRelationshipPath: (relationship: WarehouseRelationshipData) => WarehouseRelationshipPath;
  hideRelationshipTooltip: () => void;
  shouldShowRelationship: (relationship: WarehouseRelationshipData, models: WarehouseModelData) => boolean;
  showRelationshipTooltip: (relationship: WarehouseRelationshipData, triggerPoint: Point) => void;
  startHoverRelationship: (relationship: WarehouseRelationshipData) => void;
  stopHoverRelationship: () => void;
  _addTableAtCell: (table: WarehouseTableData, cell: DiagramGridCell) => void;
}

export default function useDiagramLayoutState(focusTable: WarehouseTableData): UseDiagramLayoutStateOutput {
  const [layoutState, dispatchLayoutState] = useReducer(
    diagramLayoutReducer,
    initializeDiagramLayout(focusTable)
  );

  function addRelatedTables(table: WarehouseTableData): void {
    const tableLocation = layoutState.tableNameToCell.get(table.name)!;
    const unpopulatedRelatedTables = getUnpopulatedRelatedTables(layoutState, table);

    const newTableCells = getNewTableCellsNear(
      layoutState,
      tableLocation,
      Math.min(unpopulatedRelatedTables.size, DWD_MAXIMUM_NUMBER_TABLES_TO_ADD)
    );
    const tablesToAdd = Array.from(unpopulatedRelatedTables).slice(0, DWD_MAXIMUM_NUMBER_TABLES_TO_ADD);

    dispatchLayoutState({
      type: DiagramLayoutActionType.ADD_TABLES,
      tableNames: tablesToAdd,
      cells: newTableCells,
    });
  }

  function collapseTableColumns(tableName: string): void {
    dispatchLayoutState({
      type: DiagramLayoutActionType.COLLAPSE_TABLE,
      tableName,
    });
  }

  function expandTableColumns(tableName: string): void {
    dispatchLayoutState({
      type: DiagramLayoutActionType.EXPAND_TABLE,
      tableName,
    });
  }

  function getCellTopLeftPixelPoint(cell: DiagramGridCell): DiagramPixelPoint {
    return cellToTopLeftPixel(cell);
  }

  function hideRelationshipTooltip(): void {
    dispatchLayoutState({
      type: DiagramLayoutActionType.HIDE_RELATIONSHIP_TOOLTIP,
    });
  }

  function shouldShowRelationship(
    relationship: WarehouseRelationshipData,
    models: WarehouseModelData
  ): boolean {
    return (
      columnIsShown(layoutState, relationship.source, models) &&
      columnIsShown(layoutState, relationship.target, models)
    );
  }

  function showRelationshipTooltip(relationship: WarehouseRelationshipData, triggerPoint: Point): void {
    dispatchLayoutState({
      type: DiagramLayoutActionType.SHOW_RELATIONSHIP_TOOLTIP,
      relationship,
      triggerPoint,
    });
  }

  function startHoverRelationship(relationship: WarehouseRelationshipData): void {
    dispatchLayoutState({
      type: DiagramLayoutActionType.START_HOVER_RELATIONSHIP,
      relationship,
    });
  }

  function stopHoverRelationship(): void {
    dispatchLayoutState({
      type: DiagramLayoutActionType.STOP_HOVER_RELATIONSHIP,
    });
  }

  function _addTableAtCell(table: WarehouseTableData, cell: DiagramGridCell): void {
    dispatchLayoutState({type: DiagramLayoutActionType.ADD_TABLE_AT_CELL, tableName: table.name, cell});
  }

  return {
    layout: layoutState,
    addRelatedTables,
    expandTableColumns,
    collapseTableColumns,
    cellIsObscured: (cell: DiagramGridCell, models: WarehouseModelData) =>
      cellIsObscured(layoutState, cell, models),
    getCellTopLeftPixelPoint,
    getRelationshipPath: (relationship: WarehouseRelationshipData) =>
      getRelationshipPath(layoutState, relationship),
    hideRelationshipTooltip,
    showRelationshipTooltip,
    shouldShowRelationship,
    startHoverRelationship,
    stopHoverRelationship,
    _addTableAtCell,
  };
}
